import React, { useMemo, useState } from 'react';
import { Button, Input, Space, Table, Tag, Popconfirm, Upload, message, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

export interface CreateBusinessAddressData {
  id?: number;
  type?: 'main' | 'branch';
  address?: string;
  address1?: string;
  address2?: string;
  attachment?: string;
  _isDeletedLocal?: boolean;
  _fromDeleteRequest?: boolean;
  _isNewFromRequest?: boolean;
  _addRequest?: any;
  _deleteRequest?: any;
  _crIndex?: number;
  _contactId?: number;
  _addressType?: 'main' | 'branch';
}

interface AddressTabProps {
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  addresses: CreateBusinessAddressData[];
  deletedAddresses?: Array<{ index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  addAddress: () => void;
  removeAddress: (index: number) => void;
  updateAddress: (index: number, field: keyof CreateBusinessAddressData, value: string) => void;
  getAddressDisplayValue?: (index: number, field: keyof CreateBusinessAddressData) => string;
  handleAddressFieldChange?: (index: number, field: keyof CreateBusinessAddressData, value: string) => void;
  handleApproveAddressChange?: (index: number, field: keyof CreateBusinessAddressData) => void;
  handleRejectAddressChange?: (index: number, field: keyof CreateBusinessAddressData) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveAddressDelete?: (requestId: number) => void;
  handleRejectAddressDelete?: (requestId: number) => void;
  addressFiles?: Record<string, UploadFile[]>;
  onAddressFileChange?: (index: number, file: UploadFile | null) => void;
  businessId?: number;
}

export const BusinessAddressTab: React.FC<AddressTabProps> = ({
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  addresses,
  deletedAddresses = [],
  changeRequests = [],
  addAddress,
  removeAddress,
  updateAddress,
  getAddressDisplayValue,
  handleAddressFieldChange,
  handleApproveAddressChange,
  handleRejectAddressChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveAddressDelete,
  handleRejectAddressDelete,
  addressFiles = {},
  onAddressFileChange,
  businessId,
}) => {
  const [fileActionLoading, setFileActionLoading] = useState<Record<number, { view?: boolean }>>({});

  const setFileLoading = (addressId: number, action: 'view', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [addressId]: {
        ...prev[addressId],
        [action]: isLoading,
      },
    }));
  };
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;
  const maxAddresses = 10;

  const handleView = async (addressId: number) => {
    if (!businessId || !addressId) return;

    try {
      setFileLoading(addressId, 'view', true);
      const isCustomerPortalPath = isCustomerPortal;
      const url = isCustomerPortalPath
        ? `/customer/businesses/${businessId}/addresses/${addressId}/attachment/view`
        : `/businesses/${businessId}/addresses/${addressId}/attachment/view`;
      const apiClient = isCustomerPortalPath ? customerApi : api;

      const response = await apiClient.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(addressId, 'view', false);
    }
  };

  // Get change request for a specific field
  const getFieldChangeRequest = (index: number, field: 'address1' | 'address2' | 'type', addressId?: number) => {
    return changeRequests.find(cr => {
      if (cr.status !== 'pending') return false;
      // Match pattern: address_{index}_{field}
      const pattern = `address_${index}_${field}`;
      return cr.field_name === pattern;
    });
  };

  // Get delete change requests
  const getDeleteChangeRequest = (index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `address_${index}_delete` && cr.status === 'pending'
    );
  };

  // Get add change requests
  const getAddChangeRequest = (index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `address_${index}_add` && cr.status === 'pending'
    );
  };

  // Check if an address is marked for deletion
  // Need to check by both index and id+type to handle cases where same id has multiple addresses
  const isMarkedForDeletion = (index: number, addressId?: number, addressType?: string) => {
    // Check by index first (for backward compatibility)
    const deletedByIndex = deletedAddresses.some(d => d.index === index);
    if (deletedByIndex && addressId && addressType) {
      // Also verify that the deleted address matches by id+type
      const deleted = deletedAddresses.find(d => d.index === index);
      if (deleted?.data) {
        const deletedKey = `${deleted.data.id}_${deleted.data.type || ''}`;
        const addrKey = `${addressId}_${addressType || ''}`;
        return deletedKey === addrKey;
      }
    }
    return deletedByIndex || getDeleteChangeRequest(index) !== undefined;
  };

  // Process addresses with change requests
  const processedAddresses = useMemo(() => {
    // Create a set to track which addresses (by id + type) are deleted for more accurate matching
    const deletedAddressKeys = new Set<string>();
    deletedAddresses.forEach(deleted => {
      if (deleted.data?.id) {
        // Use id + type as key to uniquely identify deleted addresses
        const key = `${deleted.data.id}_${deleted.data.type || ''}`;
        deletedAddressKeys.add(key);
      }
    });
    
    // Reference: NatureTab.tsx approach - start with all items, then add deleted ones back using splice
    // Start with all current addresses, calculating their original indices correctly
    const allAddresses: Array<any> = [];
    
    // First, add all current addresses with their calculated original indices
    // Also save the actual index in addresses array for correct field change handling
    addresses.forEach((addr, currentIdx) => {
      const addrId = addr.id || (addr as any).id;
      const addrKey = addrId ? `${addrId}_${addr.type || ''}` : '';
      // Skip if this address is marked for deletion
      if (!addrId || !deletedAddressKeys.has(addrKey)) {
        // Calculate original index: current index + number of deleted items before this position
        let originalIdx = currentIdx;
        deletedAddresses.forEach(deleted => {
          if (deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        // Save both original index (_crIndex) and actual index in addresses array (_actualIndex)
        allAddresses.push({ ...addr, _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    // Sort by _crIndex to ensure correct order before inserting deleted items
    allAddresses.sort((a, b) => a._crIndex - b._crIndex);

    // Add deleted addresses back at their original positions using splice (like FamilyTab)
    // Process deleted items in reverse order of index to avoid index shifting issues
    const sortedDeleted = [...deletedAddresses].sort((a, b) => b.index - a.index);
    sortedDeleted.forEach(deleted => {
      if (deleted.data && deleted.data.id) {
        // Check if this address is already in allAddresses by comparing id + type
        const deletedKey = `${deleted.data.id}_${deleted.data.type || ''}`;
        const exists = allAddresses.some(addr => {
          const addrId = addr.id || (addr as any).id;
          const addrKey = addrId ? `${addrId}_${addr.type || ''}` : '';
          return addrKey === deletedKey;
        });
        if (!exists) {
          // Insert at the original index position using splice (like FamilyTab)
          allAddresses.splice(deleted.index, 0, { ...deleted.data, _crIndex: deleted.index, _isDeletedLocal: true });
        }
      }
    });

    // Add new addresses from change requests (pending add requests)
    // Handle address_add format: each request contains a single address object (not an array)
    let addRequestIndex = 0;
    changeRequests.forEach(cr => {
      if (cr.status === 'pending' && cr.field_name === 'address_add') {
        // Handle address_add format: contains a single address object
        try {
          const newAddress = JSON.parse(cr.new_value || '{}');
          
          // Skip if empty
          if (!newAddress.address1 || !newAddress.address1.trim()) {
            return;
          }
          
          // Check if this address already exists in allAddresses (already processed)
          // Compare by id+type if both have id, or by type+address1+address2 if no id
          const existsInAllAddresses = allAddresses.some(addr => {
            const addrId = addr.id || (addr as any).id;
            const addrKey = addrId ? `${addrId}_${addr.type || ''}` : '';
            const newAddrId = newAddress.id || '';
            const newAddrKey = newAddrId ? `${newAddrId}_${newAddress.type || ''}` : '';
            
            if (addrKey && newAddrKey && addrKey === newAddrKey) {
              return true;
            }
            // If no id, compare by type+address1+address2
            if (!addrKey && !newAddrKey) {
              return addr.type === newAddress.type && 
                     addr.address1 === newAddress.address1 && 
                     addr.address2 === newAddress.address2;
            }
            return false;
          });
          
          // Only add if it doesn't exist in allAddresses (to avoid duplicates)
          if (!existsInAllAddresses) {
            // Use a high index to ensure these appear at the end
            const placeholder = { ...newAddress, _isNewFromRequest: true, _addRequest: cr, _crIndex: 999 + addRequestIndex };
            // Always append add-request rows to the end
            allAddresses.push(placeholder as any);
            addRequestIndex++;
          }
        } catch (e) {
          console.error('AddressTab: Error parsing address_add request:', e);
        }
      } else if (cr.status === 'pending' && cr.field_name.endsWith('_add')) {
        // Handle legacy address_{index}_add format (for backward compatibility)
        const parts = cr.field_name.split('_');
        if (parts.length >= 3) {
          const index = parseInt(parts[1], 10);
          try {
            const newAddress = JSON.parse(cr.new_value || '{}');
            const addressKey = `${newAddress.id || ''}_${newAddress.type || ''}`;
            
            // Check if this address already exists in allAddresses (already processed)
            // Compare by id+type if both have id, or by type+address1+address2 if no id
            const existsInAllAddresses = allAddresses.some(addr => {
              const addrId = addr.id || (addr as any).id;
              const addrKey = addrId ? `${addrId}_${addr.type || ''}` : '';
              const newAddrId = newAddress.id || '';
              const newAddrKey = newAddrId ? `${newAddrId}_${newAddress.type || ''}` : '';
              
              if (addrKey && newAddrKey && addrKey === newAddrKey) {
                return true;
              }
              // If no id, compare by type+address1+address2
              if (!addrKey && !newAddrKey) {
                return addr.type === newAddress.type && 
                       addr.address1 === newAddress.address1 && 
                       addr.address2 === newAddress.address2;
              }
              return false;
            });
            
            // Only add if it doesn't exist in allAddresses (to avoid duplicates)
            if (!existsInAllAddresses) {
              const placeholder = { ...newAddress, _isNewFromRequest: true, _addRequest: cr, _crIndex: index };
              // Always append add-request rows to the end
              allAddresses.push(placeholder as any);
            }
          } catch (e) {
            // Invalid JSON, skip
          }
        }
      }
    });

    const addressData = allAddresses.map((addr, idx) => {
      const crIdx = addr._crIndex !== undefined ? addr._crIndex : idx;
      // Check if this specific address (by id+type) is marked for deletion
      const addrId = addr.id || (addr as any).id;
      const addrType = addr.type || '';
      const isDeleted = addr._isDeletedLocal || 
                       (addrId && deletedAddresses.some(d => {
                         if (d.index === crIdx && d.data) {
                           const deletedKey = `${d.data.id}_${d.data.type || ''}`;
                           const addrKey = `${addrId}_${addrType}`;
                           return deletedKey === addrKey;
                         }
                         return false;
                       })) ||
                       getDeleteChangeRequest(crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(crIdx);
      return {
        ...addr,
        key: `address-${crIdx}-${idx}-${addr.id || 'new'}-${addr._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: addr._actualIndex !== undefined ? addr._actualIndex : undefined, // Save actual index in addresses array
        isDeleted: isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: addr._isNewFromRequest || false,
        addRequest: addr._addRequest,
      };
    });

    // Sort addresses by originalIndex to maintain original order (like NatureTab)
    // But also ensure: deleted < existing < pending add from request < local new row
    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 2; // pending add from backend
      if (!record.id) return 3; // local new row (not yet requested)
      return 1; // existing row
    };

    addressData.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      const idxA = a.originalIndex;
      const idxB = b.originalIndex;
      return idxA - idxB;
    });

    return addressData;
  }, [addresses, deletedAddresses, changeRequests]);

  const hasPendingFieldChange = (index: number) => {
    return changeRequests.some(
      (cr) =>
        cr.status === 'pending' &&
        cr.field_name.startsWith(`address_${index}_`) &&
        !cr.field_name.endsWith('_add') &&
        !cr.field_name.endsWith('_delete') &&
        cr.field_name !== 'address_add' // Exclude address_add (single object format)
    );
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '15%',
      render: (type: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const typeChangeRequest = getFieldChangeRequest(record.originalIndex, 'type', record.id);

        if (canEdit && !record.isDeleted) {
          let displayValue = type || '';
          if (getAddressDisplayValue) {
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getAddressDisplayValue(indexToUse, 'type');
            if (pending !== '') {
              displayValue = pending;
            }
          }
          if (typeChangeRequest && typeChangeRequest.new_value) {
            displayValue = typeChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newAddr = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = newAddr?.type || type || '';
            } catch (e) {
              displayValue = type || '';
            }
          }
          return (
            <Select
              value={displayValue}
              placeholder="Type"
              style={{ width: '100%' }}
              onChange={(val) => {
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                if (handleAddressFieldChange) {
                  handleAddressFieldChange(indexToUse, 'type', val);
                } else {
                  updateAddress(indexToUse, 'type', val);
                }
              }}
              options={[
                { label: 'Main', value: 'main' },
                { label: 'Branch', value: 'branch' },
              ]}
            />
          );
        }

        if (typeChangeRequest) {
          if (typeChangeRequest.status === 'approved') {
            return <span>{typeChangeRequest.new_value || type || '-'}</span>;
          }
          if (typeChangeRequest.status === 'rejected') {
            return <span>{typeChangeRequest.old_value || type || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {typeChangeRequest.old_value || type || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {typeChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={cellStyle}>{(type === 'main' && 'Main') || (type === 'branch' && 'Branch') || '-'}</span>;
      },
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address1',
      width: '55%',
      render: (address: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const address1ChangeRequest = getFieldChangeRequest(record.originalIndex, 'address1', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = address || record.address1 || '';
          if (address1ChangeRequest && address1ChangeRequest.new_value) {
            displayValue = address1ChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              // address_add now contains a single address object (not an array)
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const newAddr = Array.isArray(parsed) ? parsed[0] : parsed; // Backward compatibility
              displayValue = newAddr?.address || newAddr?.address1 || address || record.address1 || '';
            } catch (e) {
              displayValue = address || record.address1 || '';
            }
          } else if (getAddressDisplayValue) {
            // Use actualIndex if available (for items in addresses array), otherwise use originalIndex
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getAddressDisplayValue(indexToUse, 'address1');
            if (pending !== '') displayValue = pending;
          }
          return (
            <Input.TextArea
              value={displayValue}
              placeholder="Address"
              rows={3}
              onChange={(e) => {
                const val = e.target.value;
                // Use actualIndex if available (for items in addresses array), otherwise use originalIndex
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                if (handleAddressFieldChange) {
                  handleAddressFieldChange(indexToUse, 'address1', val);
                } else {
                  updateAddress(indexToUse, 'address1', val);
                }
              }}
            />
          );
        }

        if (address1ChangeRequest) {
          if (address1ChangeRequest.status === 'approved') {
            return <span>{address1ChangeRequest.new_value || address || record.address1 || '-'}</span>;
          }
          if (address1ChangeRequest.status === 'rejected') {
            return <span>{address1ChangeRequest.old_value || address || record.address1 || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {address1ChangeRequest.old_value || address || record.address1 || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {address1ChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }

        return <span style={{ ...cellStyle, whiteSpace: 'pre-wrap' }}>{address || record.address1 || '-'}</span>;
      },
    },
    {
      title: 'Attachment',
      key: 'attachment',
      width: '15%',
      render: (_: any, record: any) => {
        if (record.isDeleted) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        const addressId = record.id;
        const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
        const fileKey = `address_${indexToUse}`;
        const files = addressFiles[fileKey] || [];
        const hasFile = files.length > 0;
        const hasAttachment = record.attachment || (hasFile && files[0].originFileObj);

        if (!canEdit && !hasAttachment) {
          return <span>-</span>;
        }

        return (
          <Space size="small">
            {canEdit && (
              <Upload
                fileList={files}
                showUploadList={false}
                beforeUpload={() => false}
                onChange={(info) => {
                  if (onAddressFileChange) {
                    const file = info.fileList.length > 0 ? info.fileList[0] : null;
                    onAddressFileChange(indexToUse, file);
                  }
                }}
                maxCount={1}
                accept=".update,.png,.jpg,.jpeg"
              >
                <Button size="small" icon={<UploadOutlined />} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} />
              </Upload>
            )}
            {hasAttachment && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={addressId ? fileActionLoading[addressId]?.view : false}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (addressId) {
                    handleView(addressId);
                  } else {
                    const file = files[0];
                    if (file?.originFileObj) {
                      const blobUrl = window.URL.createObjectURL(file.originFileObj);
                      window.open(blobUrl, '_blank');
                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                    }
                  }
                }}
              />
            )}
            {canEdit && hasFile && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (onAddressFileChange) {
                    onAddressFileChange(indexToUse, null);
                  }
                }}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '15%',
      render: (_: any, record: any) => {
        // Handle delete request
        if (record.isDeleted && record.deleteRequest) {
          return <Tag color="orange">Pending</Tag>;
        }

        // Handle field changes from change requests
        const typeChangeRequest = getFieldChangeRequest(record.originalIndex, 'type', record.id);
        const address1ChangeRequest = getFieldChangeRequest(record.originalIndex, 'address1', record.id);
        const address2ChangeRequest = getFieldChangeRequest(record.originalIndex, 'address2', record.id);
        
        if ((typeChangeRequest || address1ChangeRequest || address2ChangeRequest) && !record.isDeleted) {
          const hasPending = (typeChangeRequest?.status === 'pending') || 
                           (address1ChangeRequest?.status === 'pending') || 
                           (address2ChangeRequest?.status === 'pending');
          
          if (hasPending) {
            return <Tag color="orange">Pending</Tag>;
          }
        }

        // Handle new address from change request (add request)
        if (record.isNewFromRequest && record.addRequest) {
          return <Tag color="orange">Pending</Tag>;
        }

        // Delete button: show for existing rows (with id) and new local rows (without id and not from request)
        // Reference: FamilyTab.tsx - new rows without id can be deleted directly
        // isNewFromRequest means it's from a pending add request, which should not be deletable via this button
        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          return (
            <Popconfirm
              title="Are you sure you want to delete this address?"
              onConfirm={() => {
                // Use actualIndex if available (for items in addresses array), otherwise use originalIndex
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                removeAddress(indexToUse);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }

        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={processedAddresses}
        pagination={false}
        size="small"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const isChanged = hasPendingFieldChange(record.originalIndex);

          // Priority: delete (red) > add (blue) > change (yellow)
          if (isDeleted) {
            return { style: { backgroundColor: '#fff1f0' } };
          }
          if (isNew) {
            return { style: { backgroundColor: '#e6f7ff' } };
          }
          if (isChanged) {
            return { style: { backgroundColor: '#fffbe6' } };
          }
          return {};
        }}
        footer={() => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {canEdit && processedAddresses.filter(a => !a.isDeleted && !a.isNewFromRequest).length < maxAddresses && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={addAddress}
                style={{ color: '#ff4d4f', padding: 0 }}
              >
                (max {maxAddresses})
          </Button>
            )}
            {!canEdit && processedAddresses.length === 0 && (
              <span style={{ color: '#999' }}>No addresses</span>
            )}
        </div>
      )}
      />
    </div>
  );
};
